import {
  ENV,
  isAdminPanel,
  isLocalhost,
  isPrDeployment,
} from '@backoffice-frontend/environment'
import { useEffect, useState } from 'react'
import { useClaims } from './useClaims'
import { CognitoIdentityProviderStages } from './useCognitoIdentityProvider'
import {
  backofficeBaseDomains,
  buildBackofficeUrl,
  getSubdomainFromHostname,
  readCachedSubdomain,
  writeCachedSubdomain,
} from './utils'

export type SubdomainCheck =
  | { status: 'checking' }
  | { status: 'match' }
  | { status: 'mismatch'; expectedUrl: string }
  | { status: 'indeterminate' }

const skipCheck = isLocalhost || isPrDeployment || isAdminPanel

const resolve = (
  expectedSubdomain: string,
  currentSubdomain: string,
  baseDomain: string,
): SubdomainCheck =>
  expectedSubdomain === currentSubdomain
    ? { status: 'match' }
    : {
        status: 'mismatch',
        expectedUrl: buildBackofficeUrl(
          expectedSubdomain,
          baseDomain,
          window.location,
        ),
      }

/**
 * The hostname does not scope any request — data is scoped by the token alone.
 * So a user on a subdomain they do not belong on sees their own organization's
 * content under a hostname claiming otherwise, and has to be stopped.
 *
 * Resolves synchronously from cache on a subdomain the user has used before, so
 * the lookup only delays first paint on a subdomain that has never been
 * confirmed — which is exactly the case worth stopping.
 *
 * `skip` lets a caller bypass the check entirely — e.g. a temporary Backoffice
 * session, whose subdomain intentionally differs from the impersonating
 * actor's own.
 */
export const useSubdomainCheck = (skip = false): SubdomainCheck => {
  const claims = useClaims()
  const email = claims.cognito.email()

  const currentSubdomain = getSubdomainFromHostname(window.location.hostname)
  const baseDomain = backofficeBaseDomains[ENV]
  const canCheck =
    !skip && !skipCheck && !!email && !!currentSubdomain && !!baseDomain

  // Tagged with the account it belongs to: claims can change while this stays
  // mounted (token refresh, or another tab signing in), and the previous
  // account's verdict must never be reported for the new one.
  const [lookup, setLookup] = useState<{
    email: string
    check: SubdomainCheck
  } | null>(null)

  const cachedSubdomain = canCheck ? readCachedSubdomain(email) : null

  useEffect(() => {
    if (!canCheck || readCachedSubdomain(email)) {
      return
    }

    const abortController = new AbortController()

    const confirmSubdomain = async () => {
      try {
        const response = await fetch(
          `${CognitoIdentityProviderStages[ENV]}/backoffice-url?email=${encodeURIComponent(email)}`,
          { signal: abortController.signal },
        )

        if (!response.ok) {
          setLookup({ email, check: { status: 'indeterminate' } })
          return
        }

        const { subdomain } = (await response.json()) as { subdomain?: string }

        if (!subdomain) {
          setLookup({ email, check: { status: 'indeterminate' } })
          return
        }

        writeCachedSubdomain(email, subdomain)
        setLookup({
          email,
          check: resolve(subdomain, currentSubdomain, baseDomain),
        })
      } catch (error) {
        if (abortController.signal.aborted) {
          return
        }
        console.error('Error resolving the backoffice url:', error)
        setLookup({ email, check: { status: 'indeterminate' } })
      }
    }

    void confirmSubdomain()

    return () => abortController.abort()
  }, [canCheck, email, currentSubdomain, baseDomain])

  if (!canCheck) {
    return { status: 'match' }
  }

  if (cachedSubdomain) {
    return resolve(cachedSubdomain, currentSubdomain, baseDomain)
  }

  return lookup?.email === email ? lookup.check : { status: 'checking' }
}
